/**
 * @name EmployeeDetail
 * @description Componente común con detalles de un empleado (avatar, nombre, detalle puesto/área/sucursal)
 * @author César Rivera
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { Avatar, Typography } from '../../../styles';

// Material-UI
import Badge from '@material-ui/core/Badge';

const EmployeeDetail = (props) => {
  const {
    avatar,
    avatarSize,
    avatarShadow,
    employeeName,
    employeeDetail,
    employeeSubDetail,
    employeeSubDetail2,
    hideAvatar,
    showDot,
    colorDot,
    orientation = 'horizontal',
  } = props;

  return (
    <Styled.FlexBox orientation={orientation}>
      {!hideAvatar && (
        <Styled.AvatarBox orientation={orientation}>
          <Styled.BadgeDot
            color="default"
            overlap="circular"
            variant="dot"
            colorDot={colorDot}
            invisible={!showDot}
          >
            <Avatar
              size={avatarSize ? avatarSize : 'xs'}
              shadow={avatarShadow ? avatarShadow : false}
              src={avatar ? avatar : 'https://assets.talentry.io/icons/profile/TAL_Avatar.png'}
            >
            </Avatar>
          </Styled.BadgeDot>
        </Styled.AvatarBox>
      )}
      <Styled.EmployeeBox orientation={orientation}>
        <Styled.EmployeeName {...props}>{employeeName}</Styled.EmployeeName>
        <Styled.EmployeeDetail {...props}>{employeeDetail}</Styled.EmployeeDetail>
        {employeeSubDetail && <Styled.EmployeeSubDetail {...props}>{employeeSubDetail}</Styled.EmployeeSubDetail>}
        {employeeSubDetail2 && <Styled.EmployeeSubDetail2 {...props}>{employeeSubDetail2}</Styled.EmployeeSubDetail2>}
      </Styled.EmployeeBox>
    </Styled.FlexBox>
  );
};

EmployeeDetail.propTypes = {
  // URL de la foto del empleado.
  avatar: PropTypes.string,
  // Tamaño de la foto del empleado.
  avatarSize: PropTypes.oneOfType(['xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', PropTypes.number]),
  // Boolean para mostrar shadow a la foto del empleado.
  avatarShadow: PropTypes.bool,
  // Label principal. (Nombre del empleado)
  employeeName: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  // Label secundario. (Puesto/Área del empleado)
  employeeDetail: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  // Label terciario. (Puesto/Área/Sucursal del empleado)
  employeeSubDetail: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  // Label cuaternario. (Puesto/Área/Sucursal del empleado)
  employeeSubDetail2: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  // Boolean que indica si se oculta el avatar del empleado.
  hideAvatar: PropTypes.bool,
  // Boolean que indica si se oculta el badge dot del avatar.
  showDot: PropTypes.bool,
  // Color del badge dot del avatar del colaborador.
  colorDot: PropTypes.string,
  // Orientación del componente.
  orientation: PropTypes.oneOf(['horizontal', 'vertical'])
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.FlexBox = styled.div`
  display: flex;
  flex: 1;
  ${(props) => props.orientation === 'horizontal' && css`flex-direction: row`};
  ${(props) => props.orientation === 'vertical' && css`flex-direction: column;`};
`;

Styled.AvatarBox = styled.div`
  align-self: center;
  ${(props) => props.orientation === 'horizontal' && css`margin-right: 16px;`};
  ${(props) => props.orientation === 'vertical' && css`margin-bottom: 16px;`};
`;

Styled.EmployeeBox = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  ${(props) => props.orientation === 'vertical' && css`text-align: center;`};
`;

Styled.EmployeeName = styled(Typography.Text)`
  font-weight: ${(props) => props.employeeSubDetail ? 'bold' : 'normal'};
`;

Styled.EmployeeDetail = styled(Typography.Text)`
  color: ${(props) => props.employeeSubDetail ? props.theme.colors.greyDark : props.theme.colors.greyDarkAlpha};
`;

Styled.EmployeeSubDetail = styled(Typography.Small)`
  color: ${(props) => props.theme.colors.greyDarkAlpha};
`;

Styled.EmployeeSubDetail2 = styled(Typography.Text)`
  color: ${(props) => props.theme.colors.greyDark};
`;

// BadgeDot 
Styled.BadgeDot = styled(Badge)`
  .MuiBadge-dot {
    background-color: ${(props) => props.colorDot ? props.colorDot : props.theme.colors.blueMedium};
  }
`;

export default EmployeeDetail;